import { useEffect } from "react";
import { datadogLogs } from "@datadog/browser-logs";
import { useGetAuthMeQuery, useGetAuthProvidersQuery, AuthMe } from "common/services";

export interface OktaSession {
  /** True while the provider toggles are still loading. */
  providersLoading: boolean;
  /**
   * True when `GET /auth/providers` failed (network error, non-2xx, etc). Callers should treat
   * this the same as "no provider confirmed enabled" — never assume a provider is available
   * just because the check couldn't complete.
   */
  providersError: boolean;
  /** Whether the backend currently offers Okta login. */
  oktaEnabled: boolean;
  /** Whether the backend currently offers direct Microsoft Entra ID login (@gerdau.com). */
  azureEnabled: boolean;
  /** Whether the backend currently offers the eGerdau path. */
  egerdauEnabled: boolean;
  /** Preferred provider for the login home page ("azure" | "okta" | undefined). */
  defaultIdp?: string;
  /** True while the `/auth/me` session check is in flight. */
  sessionLoading: boolean;
  /** True once we have a definitive answer (authenticated or not). */
  sessionChecked: boolean;
  /** The current authenticated user from `/auth/me`, when a session exists. */
  me?: AuthMe;
  /** True when a backend cookie session is active. */
  isAuthenticated: boolean;
  /** Re-runs `GET /auth/providers` — exposed so the UI can offer a retry after a failure. */
  refetchProviders: () => void;
  /** True while a `GET /auth/providers` retry (post-error) is in flight. */
  providersRetrying: boolean;
}

/**
 * Resolves the backend BFF auth state: which providers are enabled and whether the browser
 * already carries a QQT cookie session.
 *
 * <p>This is what lets QQT "understand" an Okta login established elsewhere: when the app is
 * launched from another Gerdau app that already has an Okta session, a silent SSO sets the
 * QQT cookie and `/auth/me` then returns the current user data.</p>
 */
export const useOktaSession = (): OktaSession => {
  const {
    data: providers,
    isLoading: providersLoading,
    isFetching: providersFetching,
    isError: providersError,
    error: providersFetchError,
    refetch: refetchProviders,
  } = useGetAuthProvidersQuery();
  // isFetching covers the initial load too — only surface it as a "retrying" state once we've
  // already seen an error, so the very first fetch isn't mislabeled as a retry.
  const providersRetrying = providersError && providersFetching;

  useEffect(() => {
    if (!providersFetchError) return;
    datadogLogs.logger.error("[Auth providers] GET /auth/providers failed", {
      error: providersFetchError,
    });
  }, [providersFetchError]);

  const oktaEnabled = Boolean(providers?.okta);
  const azureEnabled = Boolean(providers?.azure);
  const egerdauEnabled = Boolean(providers?.egerdau);
  const defaultIdp = providers?.defaultIdp || undefined;

  // Only probe the session once we know Okta is enabled (avoids a needless 401 otherwise).
  const {
    data: me,
    isLoading: meLoading,
    isFetching: meFetching,
    isError,
    isSuccess,
  } = useGetAuthMeQuery(undefined, { skip: providersLoading || !oktaEnabled });

  const sessionLoading = oktaEnabled && (meLoading || meFetching);
  const sessionChecked = !oktaEnabled || isSuccess || isError;

  return {
    providersLoading,
    providersError,
    oktaEnabled,
    azureEnabled,
    egerdauEnabled,
    defaultIdp,
    sessionLoading,
    sessionChecked,
    me: isSuccess ? me : undefined,
    isAuthenticated: isSuccess && Boolean(me),
    refetchProviders,
    providersRetrying,
  };
};
